import React, { useState, useEffect, useRef } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, InputAdornment, TextField, Typography, Button, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { trechoComDestaque } from "../../helpers/trechoBusca";

const MIN_CARACTERES = 2;

const useStyles = makeStyles((theme) => ({
	header: {
		display: "flex",
		alignItems: "center",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.default,
		padding: theme.spacing(0, 1),
		minHeight: "73px",
	},
	campo: { padding: theme.spacing(1.5, 1.5, 1) },
	lista: {
		flex: 1,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	item: {
		display: "block",
		width: "100%",
		textAlign: "left",
		cursor: "pointer",
		background: "none",
		border: "none",
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(1, 1.5),
		color: theme.palette.text.primary,
		"&:hover, &:focus": {
			backgroundColor: theme.palette.action.hover,
			outline: "none",
		},
	},
	meta: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: 11,
		color: theme.palette.text.secondary,
	},
	trecho: { fontSize: 13, wordBreak: "break-word" },
	destaque: {
		backgroundColor: "rgba(255, 213, 0, 0.45)",
		color: "inherit",
		borderRadius: 2,
	},
	aviso: { padding: theme.spacing(2), color: theme.palette.text.secondary, textAlign: "center" },
}));

const PainelBuscaConversa = ({ ticketId, onClose, onSelecionar }) => {
	const classes = useStyles();
	const [texto, setTexto] = useState("");
	const [resultados, setResultados] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [pagina, setPagina] = useState(1);
	const [buscando, setBuscando] = useState(false);
	const [buscou, setBuscou] = useState(false);
	// Resposta de busca antiga chegando depois da nova não pode sobrescrever a tela.
	const geracao = useRef(0);

	useEffect(() => {
		setResultados([]);
		setHasMore(false);
		setBuscou(false);
		setPagina(1);
		const termo = texto.trim();
		if (termo.length < MIN_CARACTERES) {
			geracao.current += 1;
			setBuscando(false);
			return undefined;
		}
		const minha = ++geracao.current;
		setBuscando(true);
		const t = setTimeout(async () => {
			try {
				const { data } = await api.get(`/messages/${ticketId}/busca`, { params: { q: termo, pageNumber: 1 } });
				if (minha !== geracao.current) return;
				setResultados(data.resultados);
				setHasMore(data.hasMore);
				setBuscou(true);
			} catch (err) {
				if (minha === geracao.current) toastError(err);
			} finally {
				if (minha === geracao.current) setBuscando(false);
			}
		}, 400);
		return () => clearTimeout(t);
	}, [texto, ticketId]);

	const verMais = async () => {
		const minha = geracao.current;
		const proxima = pagina + 1;
		setBuscando(true);
		try {
			const { data } = await api.get(`/messages/${ticketId}/busca`, { params: { q: texto.trim(), pageNumber: proxima } });
			if (minha !== geracao.current) return;
			setResultados((atual) => [...atual, ...data.resultados]);
			setHasMore(data.hasMore);
			setPagina(proxima);
		} catch (err) {
			toastError(err);
		} finally {
			if (minha === geracao.current) setBuscando(false);
		}
	};

	const termo = texto.trim();
	return (
		<>
			<div className={classes.header}>
				<IconButton onClick={onClose} aria-label="Fechar busca">
					<CloseIcon />
				</IconButton>
				<Typography>Pesquisar na conversa</Typography>
			</div>
			<div className={classes.campo}>
				<TextField
					autoFocus
					fullWidth
					size="small"
					variant="outlined"
					placeholder="pesquisar por..."
					value={texto}
					onChange={(e) => setTexto(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						endAdornment: buscando ? <CircularProgress size={16} /> : null,
					}}
				/>
			</div>
			<div className={classes.lista}>
				{termo.length > 0 && termo.length < MIN_CARACTERES && (
					<div className={classes.aviso}>Digite pelo menos {MIN_CARACTERES} letras.</div>
				)}
				{buscou && resultados.length === 0 && !buscando && (
					<div className={classes.aviso}>Nenhuma mensagem encontrada nesta conversa.</div>
				)}
				{resultados.map((r) => (
					<button key={r.id} type="button" className={classes.item} onClick={() => onSelecionar(r.id)}>
						<div className={classes.meta}>
							<span>{r.fromMe ? "Você" : r.contactName || "Contato"}</span>
							<span>{r.dataMensagem ? format(parseISO(r.dataMensagem), "dd/MM/yyyy HH:mm") : ""}</span>
						</div>
						<div className={classes.trecho}>
							{trechoComDestaque(r.body, termo).map((p, i) =>
								p.destaque ? (
									<mark key={i} className={classes.destaque}>{p.texto}</mark>
								) : (
									<span key={i}>{p.texto}</span>
								)
							)}
						</div>
					</button>
				))}
				{hasMore && (
					<div className={classes.aviso}>
						<Button size="small" onClick={verMais} disabled={buscando}>
							Ver mais resultados
						</Button>
					</div>
				)}
			</div>
		</>
	);
};

export default PainelBuscaConversa;
